import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  label: string;
  inputType?: "text" | "password" | "number";
  initial?: string;
  hint?: string;
  placeholder?: string;
  /** Returns a German error string when invalid, empty string when valid. */
  validate?: (value: string) => string;
  submitLabel?: string;
}

interface ActiveDialog {
  kind: "confirm" | "prompt";
  options: ConfirmOptions & Partial<PromptOptions>;
  resolve: (value: boolean | string | null) => void;
}

type ResolveFn = (value: boolean | string | null) => void;

interface DialogsValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const DialogsContext = createContext<DialogsValue | null>(null);

export function useDialogs(): DialogsValue {
  const value = useContext(DialogsContext);
  if (!value) throw new Error("Dialogs provider missing");
  return value;
}

export function DialogsProvider({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  const [dialog, setDialog] = useState<ActiveDialog | null>(null);
  const [value, setValue] = useState("");
  const [inputError, setInputError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback((result: boolean | string | null) => {
    dialog?.resolve(result);
    setDialog(null);
    setInputError("");
  }, [dialog]);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setDialog({ kind: "confirm", options, resolve: resolve as ResolveFn });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      setValue(options.initial ?? "");
      setInputError("");
      setDialog({ kind: "prompt", options, resolve: resolve as ResolveFn });
    });
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(dialog.kind === "prompt" ? null : false);
    };
    window.addEventListener("keydown", onKey);
    const focusTimer = window.setTimeout(() => {
      (dialog.kind === "prompt" ? inputRef.current : null)?.select();
    }, 30);
    return () => { window.removeEventListener("keydown", onKey); window.clearTimeout(focusTimer); };
  }, [dialog, close]);

  const submitPrompt = () => {
    if (!dialog || dialog.kind !== "prompt") return;
    const candidate = value;
    const invalid = dialog.options.validate?.(candidate) ?? "";
    if (invalid) { setInputError(invalid); return; }
    close(candidate);
  };

  return (
    <DialogsContext.Provider value={{ confirm, prompt }}>
      {children}
      {dialog && (
        <div className="admin-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(dialog.kind === "prompt" ? null : false); }}>
          <div className="admin-modal__card" role="dialog" aria-modal="true" aria-label={dialog.options.title}>
            <h2>{dialog.options.title}</h2>
            <p className="admin-modal__message">{dialog.options.message}</p>
            {dialog.kind === "prompt" && (
              <label className="nl-field">
                <span className="nl-field__label">{dialog.options.label}</span>
                <input
                  ref={inputRef}
                  className={`nl-input ${inputError ? "nl-input--error" : ""}`}
                  aria-invalid={!!inputError || undefined}
                  type={dialog.options.inputType ?? "text"}
                  value={value}
                  placeholder={dialog.options.placeholder}
                  autoFocus
                  onChange={(event) => { setValue(event.target.value); setInputError(""); }}
                  onKeyDown={(event) => { if (event.key === "Enter") submitPrompt(); }}
                />
                {(inputError || dialog.options.hint) && (
                  <span role={inputError ? "alert" : undefined} className={`nl-field__hint ${inputError ? "nl-field__hint--error" : ""}`}>{inputError || dialog.options.hint}</span>
                )}
              </label>
            )}
            <div className="admin-modal__actions">
              <Button variant="ghost" onClick={() => close(dialog.kind === "prompt" ? null : false)}>
                {dialog.options.cancelLabel ?? t("common.cancel")}
              </Button>
              {dialog.kind === "confirm" ? (
                <Button
                  variant={dialog.options.danger ? "danger" : "primary"}
                  autoFocus
                  onClick={() => close(true)}
                >
                  {dialog.options.confirmLabel ?? t("common.confirm")}
                </Button>
              ) : (
                <Button autoFocus onClick={submitPrompt}>
                  {dialog.options.submitLabel ?? t("admin.dialog.submit")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </DialogsContext.Provider>
  );
}

/** Small inline notice under the page header for action results. */
export function ActionNotice({ error, info, onDismiss }: { error?: string; info?: string; onDismiss?: () => void }) {
  const { t } = useI18n();
  if (!error && !info) return null;
  return (
    <div className={`admin-notice ${error ? "is-error" : "is-info"}`} role="status">
      <Icon name={error ? "warning" : "check"} size={16} />
      <span>{error ?? info}</span>
      {onDismiss && <button type="button" aria-label={t("common.close")} onClick={onDismiss}><Icon name="x" size={14} /></button>}
    </div>
  );
}
